import { FC, useMemo, useState } from 'react';
import { PortalHolding } from 'views/MyCellar/hooks/usePortalHoldings';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../../context/ThemeContext';
import { getRegions } from '../../../../helpers';
import { calculatePeriodReturns } from '../../../../helpers/portfolioReturns';
import { ReturnValuationPerformance } from '../Summary/hooks/useReturnValuations';

// Theme colors matching other page components
const lightThemeColors = {
  background: '#FFFFFF',
  cardBackground: '#F8FAFC',
  text: '#000000',
  textSecondary: '#666666',
  border: '#E2E8F0',
  positive: '#059669',
  negative: '#DC2626',
};

const darkThemeColors = {
  background: '#0A1628',
  cardBackground: '#0F1E32',
  text: '#FFFFFF',
  textSecondary: '#8B9CB3',
  border: '#1E3A50',
  positive: '#10B981',
  negative: '#EF4444',
};

interface RegionalValuePerformanceProps {
  holdings: PortalHolding[];
  /** Same valuation series the Performance Story chart uses, so the two agree. */
  valuations: ReturnValuationPerformance[];
  isLoading?: boolean;
  isValuationsLoading?: boolean;
}

interface RegionData {
  name: string;
  value: number;
  percentage: number;
  pnl: number;
  pnlPercent: number;
  color: string;
}

// Round an axis bound up to a readable step so the top gridline is a round number.
const niceBound = (value: number): number => {
  if (value <= 0) return 0;
  const step = value <= 2 ? 0.5 : value <= 10 ? 1 : value <= 50 ? 5 : 10;
  return Math.ceil(value / step) * step;
};

const formatAxis = (value: number): string => `${value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)}%`;

const formatCurrency = (value: number): string => {
  if (Math.abs(value) >= 1000000) {
    return `£${(value / 1000000).toFixed(3)}m`;
  }
  if (Math.abs(value) >= 1000) {
    return `£${value.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
  }
  return `£${value.toFixed(0)}`;
};

const RegionalValuePerformance: FC<RegionalValuePerformanceProps> = ({
  holdings,
  valuations,
  isLoading,
  isValuationsLoading,
}) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const themeColors = isDark ? darkThemeColors : lightThemeColors;
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [hoveredBar, setHoveredBar] = useState<string | null>(null);
  const regionColors = useMemo(() => getRegions(t), [t]);

  // Calculate regional allocation data
  const regions = useMemo(() => {
    if (!holdings || holdings.length === 0) {
      return [];
    }

    const regionMap = new Map<string, { value: number; cost: number; pnl: number }>();

    holdings.forEach((h) => {
      const region = h.cultWinesAllocationRegion || h.region || 'Other';
      const existing = regionMap.get(region) || { value: 0, cost: 0, pnl: 0 };
      regionMap.set(region, {
        value: existing.value + (h.totalValue || 0),
        cost: existing.cost + (h.totalCost || 0),
        pnl: existing.pnl + (h.pnl || 0),
      });
    });

    const total = Array.from(regionMap.values()).reduce((sum, r) => sum + r.value, 0);

    const regionData: RegionData[] = Array.from(regionMap.entries())
      .map(([name, data]) => ({
        name,
        value: data.value,
        percentage: total > 0 ? (data.value / total) * 100 : 0,
        pnl: data.pnl,
        pnlPercent: data.cost > 0 ? (data.pnl / data.cost) * 100 : 0,
        color: regionColors.find((r) => r.text === name)?.color || '#888888',
      }))
      .sort((a, b) => b.value - a.value);

    return regionData;
  }, [holdings, regionColors]);

  // Portfolio return per period, taken from the same valuation series that drives the
  // Performance Story chart so the two tell the same story. See helpers/portfolioReturns.ts.
  const performanceBars = useMemo(() => calculatePeriodReturns(valuations), [valuations]);

  // Donut chart calculations
  const donutSegments = useMemo(() => {
    let startAngle = 0;
    return regions.map((region) => {
      const angle = (region.percentage / 100) * 360;
      const segment = {
        ...region,
        startAngle,
        endAngle: startAngle + angle,
      };
      startAngle += angle;
      return segment;
    });
  }, [regions]);

  if (isLoading || isValuationsLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[0, 1].map((i) => (
          <div
            key={i}
            className="flex-1 flex flex-col rounded-lg overflow-hidden animate-pulse"
            style={{ backgroundColor: themeColors.background }}
          >
            <div className="px-5 pt-5 pb-3">
              <div className="h-6 w-48 rounded" style={{ backgroundColor: themeColors.cardBackground }} />
            </div>
            <div className="px-5 pb-5 flex justify-center">
              <div className="w-48 h-48 rounded-full" style={{ backgroundColor: themeColors.cardBackground }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!holdings || holdings.length === 0) {
    return null;
  }

  const plottedValues = performanceBars.map((b) => b.percent).filter((v): v is number => v !== null);

  // Signed scale: the axis has to be able to show gains above the zero line and losses below it.
  const upperBound = niceBound(Math.max(0, ...plottedValues));
  const lowerBound = -niceBound(Math.abs(Math.min(0, ...plottedValues)));
  const span = upperBound - lowerBound || 1;
  // Distance of the zero line from the top of the plot, as a percentage of plot height.
  const zeroLinePct = (upperBound / span) * 100;

  const hasPartialPeriod = performanceBars.some((b) => b.percent !== null && b.isPartialPeriod);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Regional Allocation - Donut Chart */}
      <div
        className="flex-1 flex flex-col rounded-lg overflow-hidden transition-colors"
        style={{ backgroundColor: themeColors.background }}
      >
        <div className="px-5 pt-5 pb-3">
          <h3 className=" text-base sm:text-lg" style={{ color: themeColors.text }}>
            Regional Allocation
          </h3>
        </div>

        <div className="px-5 pb-5 flex flex-col items-center">
          {/* Donut Chart */}
          <div className="relative w-56 h-56 mb-4">
            <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
              {donutSegments.map((segment) => {
                const radius = 38;
                const circumference = 2 * Math.PI * radius;
                const arcLength = ((segment.endAngle - segment.startAngle) / 360) * circumference;
                const dashOffset = circumference - (segment.startAngle / 360) * circumference;
                const isHovered = hoveredRegion === segment.name;

                return (
                  <circle
                    key={segment.name}
                    cx="50"
                    cy="50"
                    r={radius}
                    fill="none"
                    stroke={segment.color}
                    strokeWidth={isHovered ? 18 : 14}
                    strokeDasharray={`${arcLength} ${circumference - arcLength}`}
                    strokeDashoffset={dashOffset}
                    className="transition-all duration-200 cursor-pointer"
                    style={{ filter: isHovered ? 'brightness(1.1)' : 'none' }}
                    onMouseEnter={() => setHoveredRegion(segment.name)}
                    onMouseLeave={() => setHoveredRegion(null)}
                  />
                );
              })}
            </svg>

            {/* Hover Tooltip */}
            {hoveredRegion && (
              <div
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 px-4 py-3 rounded-lg shadow-lg text-center pointer-events-none z-10"
                style={{
                  backgroundColor: isDark ? themeColors.cardBackground : '#FFFFFF',
                  border: `1px solid ${themeColors.border}`,
                }}
              >
                <div className="text-base sm:text-lg" style={{ color: themeColors.text }}>
                  {hoveredRegion}
                </div>
                <div className="text-base" style={{ color: themeColors.textSecondary }}>
                  {formatCurrency(regions.find((r) => r.name === hoveredRegion)?.value || 0)} (
                  {regions.find((r) => r.name === hoveredRegion)?.percentage.toFixed(1)}%)
                </div>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2">
            {regions.map((region) => (
              <div
                key={region.name}
                className="flex items-center gap-2 cursor-pointer"
                onMouseEnter={() => setHoveredRegion(region.name)}
                onMouseLeave={() => setHoveredRegion(null)}
              >
                <div className="w-3.5 h-3.5 rounded-sm" style={{ backgroundColor: region.color }} />
                <span className="text-sm font-medium" style={{ color: themeColors.text }}>
                  {region.name} ({region.percentage.toFixed(1)}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Value-Weighted Performance - Bar Chart */}
      <div
        className="flex-1 flex flex-col rounded-lg overflow-hidden transition-colors"
        style={{ backgroundColor: themeColors.background }}
      >
        <div className="px-5 pt-5 pb-3">
          <h3 className="text-base sm:text-lg" style={{ color: themeColors.text }}>
            Portfolio Performance
          </h3>
          <p className="text-sm mt-1" style={{ color: themeColors.textSecondary }}>
            Return after money paid in and taken out, on the same basis as the Performance Story
            {hasPartialPeriod ? ' · some periods cover the full portfolio history only' : ''}
          </p>
        </div>

        <div className="px-5 pb-5 flex-1 flex flex-col justify-center">
          {/* Y-axis labels and bars container */}
          <div className="flex gap-4">
            {/* Y-axis */}
            <div className="relative h-48 w-12 text-right pr-2">
              {upperBound > 0 && (
                <span
                  className="absolute right-2 text-sm -translate-y-1/2"
                  style={{ top: 0, color: themeColors.textSecondary }}
                >
                  {formatAxis(upperBound)}
                </span>
              )}
              <span
                className="absolute right-2 text-sm -translate-y-1/2"
                style={{ top: `${zeroLinePct}%`, color: themeColors.textSecondary }}
              >
                0%
              </span>
              {lowerBound < 0 && (
                <span
                  className="absolute right-2 text-sm -translate-y-1/2"
                  style={{ top: '100%', color: themeColors.textSecondary }}
                >
                  {formatAxis(lowerBound)}
                </span>
              )}
            </div>

            {/* Bars */}
            <div className="flex-1 flex items-start justify-around relative h-48 min-w-0">
              {/* Zero line */}
              <div
                className="absolute left-0 right-0 h-px"
                style={{ top: `${zeroLinePct}%`, backgroundColor: themeColors.border }}
              />

              {performanceBars.map((bar) => {
                const isHovered = hoveredBar === bar.label;

                if (bar.percent === null) {
                  return (
                    <div
                      key={bar.label}
                      className="flex items-center justify-center flex-1 h-full min-w-0 relative"
                      title={`Not enough history to show a ${bar.label} return`}
                    >
                      <span className="text-xs" style={{ color: themeColors.textSecondary }}>
                        n/a
                      </span>
                    </div>
                  );
                }

                const isNegative = bar.percent < 0;
                const barColor = isNegative ? themeColors.negative : themeColors.positive;

                // Positive bars grow up from the zero line, negative bars grow down from it.
                const barHeight = isNegative
                  ? (bar.percent / lowerBound) * (100 - zeroLinePct)
                  : (upperBound > 0 ? bar.percent / upperBound : 0) * zeroLinePct;
                const barPosition = isNegative ? { top: `${zeroLinePct}%` } : { top: `${zeroLinePct - barHeight}%` };

                return (
                  <div
                    key={bar.label}
                    className="flex flex-col items-center flex-1 h-full min-w-0 cursor-pointer relative"
                    onMouseEnter={() => setHoveredBar(bar.label)}
                    onMouseLeave={() => setHoveredBar(null)}
                  >
                    {/* Bar */}
                    <div
                      className="absolute w-8 sm:w-12 lg:w-16 rounded-sm transition-all duration-200"
                      style={{
                        ...barPosition,
                        height: `${Math.abs(barHeight)}%`,
                        backgroundColor: barColor,
                        filter: isHovered ? 'brightness(1.1)' : 'none',
                        transform: isHovered ? 'scaleX(1.1)' : 'scaleX(1)',
                      }}
                    />
                    {/* Hover Tooltip */}
                    {isHovered && (
                      <div
                        className="absolute left-1/2 transform -translate-x-1/2 px-3 py-2 rounded-lg shadow-lg text-center pointer-events-none z-10 whitespace-nowrap"
                        style={{
                          top: isNegative ? `calc(${zeroLinePct + Math.abs(barHeight)}% + 4px)` : undefined,
                          bottom: isNegative ? undefined : `calc(${100 - zeroLinePct + barHeight}% + 4px)`,
                          backgroundColor: isDark ? themeColors.cardBackground : '#FFFFFF',
                          border: `1px solid ${themeColors.border}`,
                        }}
                      >
                        <div className="text-sm font-medium" style={{ color: barColor }}>
                          {bar.percent >= 0 ? '+' : ''}
                          {bar.percent.toFixed(2)}%
                        </div>
                        {bar.isPartialPeriod && (
                          <div className="text-xs" style={{ color: themeColors.textSecondary }}>
                            {bar.monthsUsed} month{bar.monthsUsed === 1 ? '' : 's'} of history
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Period labels, aligned under the bars */}
          <div className="flex gap-4 mt-2">
            <div className="w-12" />
            <div className="flex-1 flex justify-around min-w-0">
              {performanceBars.map((bar) => (
                <span
                  key={bar.label}
                  className="flex-1 text-center text-sm font-medium"
                  style={{ color: themeColors.text }}
                >
                  {bar.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegionalValuePerformance;
